import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {WholesalerIntegrationStepsEnum} from '../../../../interfaces/WholesalerIntegrationStepsEnum';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {AlertTypesEnum} from '../../../../../shared/interfaces/AlertTypesEnum';
import {Select, Store} from '@ngxs/store';
import {ProfileState} from '../../../../../shared/states/profile/profile.state';
import {Observable} from 'rxjs/internal/Observable';
import {IWholesalerConnection} from '../../../../../shared/interfaces/wholesaler-integration.interface';
import {GetAvailableWholesalersList, GetWholesalersList, SetPharmacySelectedWholesaler, TestWholesalerConnection} from '../../../../../shared/states/profile/profile.actions';
import {IAlertMessage} from '../../../../../shared/interfaces/alert-message.interface';
import {take, withLatestFrom} from 'rxjs/operators';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';

@Component({
  selector: 'app-test-connection',
  templateUrl: './test-connection.component.html',
  styleUrls: ['./test-connection.component.scss'],
})
export class TestConnectionComponent implements OnInit {
  @Input() wholesalerIntegration: IWholesalerConnection | undefined;
  @Input() progress: WholesalerIntegrationStepsEnum;

  @Output() back: EventEmitter<WholesalerIntegrationStepsEnum> = new EventEmitter();
  @Output() next: EventEmitter<WholesalerIntegrationStepsEnum> = new EventEmitter();

  @Output() save: EventEmitter<any> = new EventEmitter();

  connectionForm: UntypedFormGroup;

  WholesalerIntegrationStepsEnum = WholesalerIntegrationStepsEnum;

  alertMessage: IAlertMessage;
  autohideDelay = 3;
  showMessage: boolean;
  AlertTypesEnum = AlertTypesEnum;

  focusWholesalerSearch: boolean;

  @Select(ProfileState.loading) loading$: Observable<boolean | undefined>;
  @Select(ProfileState.success) success$: Observable<boolean | undefined>;

  @ViewChild('serverURLInput') serverURLInput;
  @ViewChild('ftpUsernameInput') ftpUsernameInput;
  @ViewChild('ftpPasswordInput') ftpPasswordInput;
  @ViewChild('ftpPortInput') ftpPortInput;
  @ViewChild('inputPathInput') inputPathInput;
  @ViewChild('outputPathInput') outputPathInput;
  @ViewChild('wholesalerDropdown') wholesalerDropdown;
  @ViewChild('wanInput') wanInput;
  @ViewChild('testConnectionButton') testConnectionButton;

  autoFilledProps = this.getAutoFilledPropsInitialValue();

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
  ) {
    this.connectionForm = this.fb.group({
      id: '',
      ftpUrl: ['', [Validators.required]],
      ftpUsername: ['', [Validators.required]],
      ftpPassword: ['', [Validators.required]],
      outputPath: ['', [Validators.required]],
      wholesalerName: ['', [Validators.required]],
      wholesalerId: '',
      accountNumber: ['', [Validators.required]],
      isActive: true,
      ftpPort: ['22', [Validators.required]],
      defaultInventoryLocationID: this.store.selectSnapshot(InventoryState.primaryLocationID)
    });
  }

  ngOnInit(): void {
    this.store.dispatch(new GetAvailableWholesalersList(this.wholesalerIntegration?.id));

    if (this.wholesalerIntegration) {
      this.connectionForm.patchValue(this.wholesalerIntegration);
      this.store.dispatch(new SetPharmacySelectedWholesaler(this.wholesalerIntegration));
    }
    this.focusWholesalerSearch = true;
  }

  testConnection(): void {
    if (this.connectionForm.valid) {
      this.store.dispatch(new TestWholesalerConnection(this.connectionForm.value))
        .pipe(take(1), withLatestFrom(this.success$))
        .subscribe(([_, success]) => {
          if (success) {
            this.progress = WholesalerIntegrationStepsEnum.Finish;
            this.next.emit(WholesalerIntegrationStepsEnum.Finish);
            this.connectionForm.disable();
            this.alertMessage = {text: 'Success!', type: AlertTypesEnum.Success};
          } else {
            this.alertMessage = {text: 'Error connecting. Please try again.', type: AlertTypesEnum.Error};
          }

          this.showMessage = true;
        });
    }
    else {
      highlightRequiredControlsWithErrors(this.connectionForm);
    }
  }

  backToConnection(): void {
    this.showMessage = false;
    this.connectionForm.enable();
    this.back.emit(WholesalerIntegrationStepsEnum.PendingConnection);
  }

  focusInput(inputName: string): void {
    setTimeout(() => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus());
  }

  focusDropdown(name: string): void {
    setTimeout(() => {
      this[name].showDropdown = true;
      setTimeout(() => this[name].dropdownItemsQueryList?.toArray()[0]?.nativeElement.focus());
    });
  }

  focusButton(buttonName: string): void {
    setTimeout(() => this[buttonName]?.focus());
  }

  chosenWholesaler(option: IWholesalerConnection): void {
    setTimeout(() => this.focusInput('wanInput'), 200);
  }

  wholesalerSelected(wholesaler): void {
    this.store.dispatch(new SetPharmacySelectedWholesaler(wholesaler));
    const autofill = this.store.selectSnapshot(ProfileState.wholesalers).find(x => x.wholesalerStandard?.wholesalerId == wholesaler?.id)?.wholesalerStandard;
    this.connectionForm.get("wholesalerId").setValue(wholesaler?.id);
    this.connectionForm.get("wholesalerName").setValue(wholesaler?.name);
    this.connectionForm.patchValue(autofill);
    //UN and PWD for some wholesalers
    this.setValueForCredentials(wholesaler);

    this.autoFilledProps.ftpUrl = !!autofill?.ftpUrl;
    this.autoFilledProps.outputPath = !!autofill?.outputPath;
    this.autoFilledProps.ftpPort = !!autofill?.ftpPort;
  }

  wholesalerSearchCleared(): void {
    this.connectionForm.reset({defaultInventoryLocationID: this.store.selectSnapshot(InventoryState.primaryLocationID)});
    this.autoFilledProps = this.getAutoFilledPropsInitialValue();
  }

  wholesalerSearchValueChanged(val: string): void {
    if(val === '') {
      this.connectionForm.patchValue({ftpUrl: '', ftpUsername: '', ftpPassword: '',  outputPath: '', accountNumber: '', ftpPort: ''});
      this.autoFilledProps = this.getAutoFilledPropsInitialValue();
    }
     
  }
 //TODO: this is something needs to be refactored
  private setValueForCredentials(wholesaler): void {
      if(wholesaler?.name?.includes("ASSOCIATED PHARMACIES")) {
        const creds = this.store.selectSnapshot(ProfileState)?.wholesalerCredentials;
        this.connectionForm.get('ftpUsername').setValue(creds?.associatedPharmaciesUN);
        this.connectionForm.get('ftpPassword').setValue(creds?.associatedPharmaciesPWD);
        this.autoFilledProps.ftpUsername = !!creds?.associatedPharmaciesUN;
        this.autoFilledProps.ftpPassword = !!creds?.associatedPharmaciesPWD;
      } else if(wholesaler?.name?.includes("INDEPENDENT PHARMACY DISTRIBUTOR")) {
        const creds = this.store.selectSnapshot(ProfileState)?.wholesalerCredentials;
        this.connectionForm.get('ftpPassword').setValue(creds?.independentPharmacyDistributorPWD);
        this.autoFilledProps.ftpPassword = !!creds?.independentPharmacyDistributorPWD;
      } else if (wholesaler?.name?.includes("ANDA PHARMACEUTICALS")) {
        const creds = this.store.selectSnapshot(ProfileState)?.wholesalerCredentials;
        this.connectionForm.get('ftpUsername').setValue(creds?.andaUN);
        this.connectionForm.get('ftpPassword').setValue(creds?.andaPWD);
        this.autoFilledProps.ftpUsername = !!creds?.andaUN;
        this.autoFilledProps.ftpPassword = !!creds?.andaPWD;
      }
    }

    private getAutoFilledPropsInitialValue(): any {
      return {
        ftpUrl: false,
        outputPath: false,
        ftpPort: false,
        ftpUsername: false,
        ftpPassword: false
      };
    }
}

  

